import { useEffect, useState } from 'react';
import useResizeObserver from 'use-resize-observer';

import { dpr } from 'Utils/dpr';
import { getNewDimensionsWithAspectRatio } from 'Utils/getNewDimensionsWithAspectRatio';
import { BreakPointHooks } from 'Hooks/BreakPointHooks';
import useLockedBody from 'Hooks/useLockedBody';
import useScrollPosition from 'Hooks/useScrollPosition';

import { IHeaderViewModel } from 'Types/Litium';
import CountryAndContact from 'Components/Header/CountryAndContact';
import DisturbanceBanner from 'Components/Header/DisturbanceBanner';
import HamburgerButton from 'Components/Header/HamburgerButton';
import HideOnScrollWrapper from 'Components/Header/HideOnScrollWrapper';
import MegaMenu from 'Components/Header/MegaMenu';
import MiniCartButton from 'Components/Header/MiniCartButton';
import CustomerServiceCartButton from 'Components/Header/CustomerServiceCartButton';
import MobileMenu from 'Components/Header/MobileMenu';
import QuickSearch from 'Components/Header/QuickSearch';
import UspBanner from 'Components/Header/UspBanner';
import ImpersonationBanner from 'Components/Header/ImpersonationBanner';
import ScrollToTopButton from 'Components/ScrollToTopButton';
import SkipNavLinks from 'Components/SkipNavLinks';

import HeartIcon from 'Icons/heart.svg?react';
import ProfileIcon from 'Icons/profile.svg?react';

import { GlobalToEuropeRedirectPopUp } from './GlobalToEuropeRedirectPopUp';

const { useGreater } = BreakPointHooks();

const Header = ({
    logo,
    logoMini,
    loginPage,
    favoritesPage,
    checkoutPage,
    startPageUrl,
    disturbanceBanner,
    uspBanner,
    navigation,
    quickSearch,
    currentCulture,
    additionalLinkList,
    mobileMenuEvergreenLinkList,
    translations,
    globalToEuropeRedirectPopUp,
    minimizedDesktopHeader,
    canActAsOtherUser,
    impersonationBanner
}: IHeaderViewModel) => {
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showStayOnGlobalCheckPopUp, setShowStayOnGlobalCheckPopUp] =
        useState(true);
    const pos = useScrollPosition();

    const { ref: headerRef, height: headerHeight = 1 } =
        useResizeObserver<HTMLDivElement>();

    const [, setLocked] = useLockedBody(false);
    const isGreater = useGreater('lg');

    const slimHeader =
        minimizedDesktopHeader || pos >= headerHeight * 2 ? 'slimHeader' : '';

    const onHamburgerClick = (state: boolean) => {
        setShowMobileMenu(state);
        setLocked(state);
    };

    const globalToEuropeRedirectPopUpCookieName =
        'global_to_europe_redirect_dismissed';

    const handleGlobalToEuropeRedirectClose = () => {
        document.cookie = `${globalToEuropeRedirectPopUpCookieName}=true;max-age=16200000`;
        setShowStayOnGlobalCheckPopUp(false);
    };

    useEffect(() => {
        if (isGreater) {
            setShowMobileMenu(false);
            setLocked(false);
        }
        const globalToEuropeRedirectCookieValue = document.cookie
            .split('; ')
            .find(row =>
                row.startsWith(`${globalToEuropeRedirectPopUpCookieName}=`)
            )
            ?.split('=')[1];

        if (globalToEuropeRedirectCookieValue) {
            setShowStayOnGlobalCheckPopUp(false);
        }
    }, [isGreater]);
    return (
        <header
            ref={headerRef}
            className={`group bg-white shadow-minimal ${slimHeader}`}
        >
            {canActAsOtherUser && impersonationBanner && (
                <ImpersonationBanner
                    translations={impersonationBanner.translations}
                    currentUser={impersonationBanner.currentUser}
                    isImpersonating={impersonationBanner.isImpersonating}
                    loginPage={loginPage}
                >
                </ImpersonationBanner>
            )}
            <SkipNavLinks
                mainContentTranslation={translations.skipToMainContent}
                quickSearchTranslation={translations.skipToQuickSearch}
                footerTranslation={translations.skipToFooter}
                mainContentId="main-content"
                quickSearchId="q"
                footerId="footer"
            />
            {(disturbanceBanner || uspBanner) && (
                <HideOnScrollWrapper>
                    <div className="group-[.headerScroll]:hidden">
                        {disturbanceBanner && (
                            <DisturbanceBanner
                                textWidthMobile={
                                    disturbanceBanner?.textWidthMobile
                                }
                                text={disturbanceBanner?.text}
                                backgroundColor={
                                    disturbanceBanner?.backgroundColor
                                }
                                closeBannerText={
                                    disturbanceBanner?.closeBannerText
                                }
                            />
                        )}
                        {uspBanner && (
                            <UspBanner
                                text={uspBanner?.text}
                                backgroundColor={uspBanner?.backgroundColor}
                                showBannerOnMobile={
                                    uspBanner?.showBannerOnMobile
                                }
                            />
                        )}
                    </div>
                </HideOnScrollWrapper>
            )}
            <div className="relative mx-auto flex max-w-content justify-between px-4.5 pb-[64px] pt-5 transition-[padding] duration-layout lg:block lg:px-14 lg:py-5 lg:pb-0 lg:group-[.slimHeader]:py-0">
                <div className="flex w-full gap-3.5 md:gap-6 lg:justify-between lg:gap-0">
                    <HamburgerButton
                        isActive={showMobileMenu}
                        onClickHandler={() => onHamburgerClick(!showMobileMenu)}
                        translations={translations}
                    />
                    <a
                        className="flex items-center gap-2.5 font-montserrat text-xs lg:hidden"
                        href={loginPage?.href}
                    >
                        <ProfileIcon className="h-6 w-6" />
                        <span className="sr-only lg:hidden">
                            {loginPage?.text}
                        </span>
                    </a>
                    <HideOnScrollWrapper
                        desktopOnly
                        className="basis-full items-center"
                    >
                        <div className="flex justify-center lg:justify-start">
                            {logo?.src && (
                                <a href={startPageUrl}>
                                    <img
                                        className={`h-6 lg:h-[46px] ${
                                            logo?.src.includes('.svg')
                                                ? ''
                                                : 'h-auto max-h-6 w-auto lg:max-h-[46px]'
                                        }`}
                                        src={logo?.src}
                                        srcSet={dpr(
                                            `${logo?.src}?format=webp&height=46`
                                        )}
                                        alt={logo?.altText}
                                        width={
                                            logo?.width > 0
                                                ? getNewDimensionsWithAspectRatio(
                                                      logo.width,
                                                      logo.height,
                                                      46,
                                                      'width'
                                                  )
                                                : undefined
                                        }
                                        height={
                                            logo?.height > 0 ? 46 : undefined
                                        }
                                    />
                                </a>
                            )}
                        </div>
                    </HideOnScrollWrapper>
                    <HideOnScrollWrapper className="flex-shrink-0 flex-grow-0">
                        <div className="flex h-full">
                            <CountryAndContact
                                currentCulture={currentCulture}
                                linkList={additionalLinkList}
                            />
                        </div>
                    </HideOnScrollWrapper>
                </div>
                <div className="inline-flex items-center justify-between lg:flex">
                    <MegaMenu
                        contentLinks={navigation.contentLinks}
                        logoMini={logoMini}
                        startPageUrl={startPageUrl}
                        showMiniLogoBreakPoint={!!slimHeader}
                    />
                    <ul className="flex flex-shrink-0 flex-nowrap gap-3.5 md:gap-6 lg:items-center lg:gap-5 xl:mb-0">
                        <li className="absolute bottom-0 left-0 z-10 w-full bg-white pb-3 lg:static lg:pb-0 xl:static xl:pb-0">
                            <QuickSearch
                                searchTitle={quickSearch.quickSearchPlaceholder}
                                searchResultPageUrl={
                                    quickSearch.searchResultUrl
                                }
                                translations={quickSearch.translations}
                            />
                            <MobileMenu
                                isOpen={showMobileMenu}
                                offset={headerHeight}
                                menuItems={navigation.contentLinks}
                                currentCulture={currentCulture}
                                linkList={additionalLinkList}
                                evergreenLinkList={mobileMenuEvergreenLinkList}
                            />
                        </li>
                        {loginPage?.href && (
                            <li className="hidden lg:block">
                                <a
                                    aria-label={loginPage?.text}
                                    className="group/link flex items-center gap-2.5 font-montserrat text-sm font-normal text-black lg:pb-4.5 lg:pt-4"
                                    href={loginPage.href}
                                >
                                    <ProfileIcon className="h-6 w-6" />
                                </a>
                            </li>
                        )}

                        {favoritesPage?.href ? (
                            <li>
                                <a
                                    aria-label={favoritesPage?.text}
                                    className="group/link flex items-center gap-2.5 font-montserrat text-sm font-normal text-black lg:pb-4.5 lg:pt-4"
                                    href={favoritesPage.href}
                                >
                                    <HeartIcon className="h-6 w-[28px]" />
                                </a>
                            </li>
                        ) : (
                            <li className="lg:hidden">
                                <div className="group/link flex items-center gap-2.5">
                                    <div className="h-6 w-6" />
                                </div>
                            </li>
                        )}

                        <li className="flex items-center">
                            {(canActAsOtherUser && impersonationBanner) ? (
                                <CustomerServiceCartButton
                                    miniCartOpenText={translations.minicartOpen}
                                />
                            ) : (
                                <MiniCartButton
                                    checkoutPage={checkoutPage}
                                    miniCartOpenText={translations.minicartOpen}
                                />
                            )}
                        </li>
                    </ul>
                </div>
            </div>
            {showStayOnGlobalCheckPopUp &&
                globalToEuropeRedirectPopUp &&
                globalToEuropeRedirectPopUp.redirectToEuropeLink && (
                    <GlobalToEuropeRedirectPopUp
                        globalToEuropeRedirectPopUpModel={
                            globalToEuropeRedirectPopUp
                        }
                        isOpened={showStayOnGlobalCheckPopUp}
                        onClose={() => handleGlobalToEuropeRedirectClose()}
                    />
                )}
            <ScrollToTopButton
                translations={{ scrollToTop: translations.scrollToTop }}
            />
        </header>
    );
};

export default Header;
